import {
  dateFromDateString,
  dateFromTimeString,
  DateString,
  dayMonthDateString,
  durationString,
  getDuration,
  getDurationinMinutes,
  Shift,
  ShiftUpdateParams,
  Store,
  timeRangeString,
  TimeString,
  timeToNearestIncrement,
  toDateString,
  toTimeString,
} from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import moment from 'moment'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SectionList, SectionListData, View  } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { confirmationAlert } from 'shared/alerts'
import { DateTimeChooser } from 'shared/components/list/date_time_chooser'
import { ValueChooser } from 'shared/components/list/value_chooser'
import { RoundedButton } from 'shared/components/rounded-button'
import {
  getEndDateTime,
  getShiftEndDateTime,
  getShiftStartDateTime,
  getStartDateTime,
} from 'shifts/utils'
import { DEFAULT_STORE_PREFERENCES } from 'stores/constants'
import { Item } from './item'

const SHIFT_COPIES = [1, 2, 3, 4, 5]
interface Props {
  store: Store | undefined
  shift?: Shift
  onSave(splitShift?: boolean): Promise<void>
  onSelectCrew(): void
  isAddAndAssign: boolean
  onShiftParamsChanged(shiftData: ShiftUpdateParams): void
  loading: boolean
}

type Section = SectionListData<
  Item,
  {
    name: string
    title?: string
  }
>

export function EditShiftView({
  store,
  shift,
  onSave,
  onSelectCrew,
  isAddAndAssign,
  onShiftParamsChanged,
  loading,
}: Props) {
  const styles = useStyles()
  const preferences = store?.preferences ?? DEFAULT_STORE_PREFERENCES
  const newShiftDefaults = store?.preferences?.newShiftDefaults
  const currentDateAtStore = dateFromDateString(
    toDateString(moment()),
    store?.tz
  )

  const currentTimeAtStore = timeToNearestIncrement(
    dateFromTimeString(toTimeString(moment()), store?.tz),
    preferences.minShiftDuration
  )

  const [startTime, setStartTime] = useState<TimeString>(
    shift?.startTime ??
      newShiftDefaults?.startTime ??
      toTimeString(currentTimeAtStore)
  )

  const [date, setDate] = useState<DateString>(
    toDateString(
      shift?.date ??
        (newShiftDefaults?.cutoffTime &&
        moment(currentTimeAtStore).diff(
          moment(newShiftDefaults.cutoffTime, 'HH:mm')
        ) > 0
          ? currentDateAtStore.setDate(currentDateAtStore.getDate() + 1)
          : currentDateAtStore)
    )
  )

  const [endTime, setEndTime] = useState<TimeString>(
    shift?.endTime ??
      toTimeString(
        moment(startTime, 'HH:mm').add(
          newShiftDefaults?.duration
            ? moment.duration(newShiftDefaults.duration, 'minutes')
            : moment.duration(preferences.minShiftDuration, 'minutes')
        )
      )
  )

  const defaultPosition = store?.positions?.find(
    p => p.id === newShiftDefaults?.positionId?.toString()
  )

  const [positionName, setPositionName] = useState(defaultPosition?.name)
  const [position, setPosition] = useState(defaultPosition)
  const [copies, setCopies] = useState<number>(newShiftDefaults?.copies ?? 1)
  const [reason, setReason] = useState(newShiftDefaults?.initiatorNote)

  useEffect(() => {
    onShiftParamsChanged({
      date,
      startTime,
      endTime,
      position,
      reason,
      copies,
    })
  }, [copies, date, endTime, onShiftParamsChanged, position, reason, startTime])

  const sections = useMemo(() => {
    const positionSection: Section = {
      name: 'position',
      title: isAddAndAssign
        ? t('assignShiftPositionPrompt')
        : t('shiftPositionPrompt'),
      data: [
        {
          type: 'position',
          key: 'position',
        },
        {
          type: 'copies',
          key: 'copies',
        },
      ],
    }

    const reasonSection: Section = {
      name: 'reason',
      title: t('shiftReasonPrompt'),
      data: [
        {
          type: 'reason',
          key: 'reason',
        },
      ],
    }

    const dateTimeSection: Section = {
      name: 'dateTime',
      title: t('shiftDateTimePrompt'),
      data: [
        {
          type: 'date',
          key: 'date',
        },
        {
          type: 'startTime',
          key: 'startTime',
        },
        {
          type: 'endTime',
          key: 'endTime',
        },
      ],
    }

    return [
      ...(!shift ? [positionSection] : []),
      dateTimeSection,
      ...(!shift && !isAddAndAssign ? [reasonSection] : []),
    ]
  }, [isAddAndAssign, shift])

  const onDateChange = useCallback((selectedDate: Date) => {
    setDate(toDateString(selectedDate))
  }, [])

  const onStartTimeChange = useCallback((selectedStartTime: Date) => {
    const startTimeString = toTimeString(selectedStartTime)
    setStartTime(startTimeString)
  }, [])

  const onEndTimeChange = useCallback((selectedEndTime: Date) => {
    const endTimeString = toTimeString(selectedEndTime)
    setEndTime(endTimeString)
  }, [])

  const onPositionNameChanged = useCallback(
    (selectedPositionName: string) => {
      if (!store) {
        return
      }
      setPosition(
        store.positions?.find(p => p.displayName === selectedPositionName)
      )
      setPositionName(selectedPositionName)
    },
    [store]
  )

  const renderItem = useCallback(
    ({ item }: { item: Item }) => {
      if (!store) {
        return null
      }

      const maxDate = moment()
        .add(moment.duration(preferences.maxShiftDays, 'days'))
        .toDate()

      switch (item?.type) {
        case 'position':
          return (
            <ValueChooser
              title={t('position')}
              value={positionName}
              setValue={onPositionNameChanged}
              required={!preferences.allowEmptyPosition || isAddAndAssign}
              items={store.positions?.map(p => p.displayName) ?? []}
            />
          )
        case 'copies':
          return !isAddAndAssign ? (
            <ValueChooser
              title={t('howMany')}
              value={copies}
              setValue={setCopies}
              items={SHIFT_COPIES}
              required
            />
          ) : null
        case 'date':
          return (
            <DateTimeChooser
              title={t('date')}
              value={dateFromDateString(date)}
              mode="date"
              maxDate={maxDate}
              disabled={!!shift}
              onChange={onDateChange}
            />
          )
        case 'startTime':
          return (
            <DateTimeChooser
              title={t('startTime')}
              value={dateFromTimeString(startTime)}
              mode="time"
              minuteInterval={preferences.shiftIncrements}
              onChange={onStartTimeChange}
            />
          )
        case 'endTime':
          return (
            <DateTimeChooser
              title={t('endTime')}
              value={dateFromTimeString(endTime)}
              mode="time"
              minuteInterval={preferences.shiftIncrements}
              onChange={onEndTimeChange}
            />
          )
        case 'reason':
          return (
            <ValueChooser
              title={t('reason')}
              value={reason}
              setValue={setReason}
              required
              items={store.reasons ?? []}
            />
          )
        default:
          return null
      }
    },
    [
      isAddAndAssign,
      copies,
      date,
      endTime,
      onDateChange,
      onEndTimeChange,
      onPositionNameChanged,
      onStartTimeChange,
      positionName,
      preferences.allowEmptyPosition,
      preferences.maxShiftDays,
      preferences.shiftIncrements,
      reason,
      shift,
      startTime,
      store,
    ]
  )

  const canSave = useMemo(() => {
    if (shift) {
      return (
        !moment(getStartDateTime(date, startTime)).isSame(
          getShiftStartDateTime(shift)
        ) ||
        !moment(getEndDateTime(date, startTime, endTime)).isSame(
          getShiftEndDateTime(shift)
        )
      )
    }

    if (!isAddAndAssign && !reason?.length) {
      return false
    }

    if (!isAddAndAssign && preferences.allowEmptyPosition) {
      return true
    }

    return !!positionName?.length
  }, [
    isAddAndAssign,
    date,
    endTime,
    positionName?.length,
    preferences.allowEmptyPosition,
    reason?.length,
    shift,
    startTime,
  ])

  const onSaveShift = useCallback(
    async (splitShift: boolean) => {
      if (!store) {
        return
      }

      if (!shift && !reason && !isAddAndAssign) {
        return
      }

      if (isAddAndAssign) {
        return onSelectCrew()
      }

      await onSave(splitShift)
    },
    [isAddAndAssign, onSave, onSelectCrew, reason, shift, store]
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.container}>
      <SectionList
        sections={sections}
        renderItem={renderItem}
        renderSectionHeader={({ section }) =>
          section.title ? (
            <Text style={styles.sectionHeaderText}>
              {section.title.toUpperCase()}
            </Text>
          ) : null
        }
        stickySectionHeadersEnabled={false}
        extraData={[positionName, reason]}
      />
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          {!shift
            ? t('multipleShifts', {
                count: copies,
                positionName: positionName?.length ? positionName : '',
              })
            : null}
        </Text>
        <Text style={styles.footerText}>
          {`${dayMonthDateString(date)} ${timeRangeString(
            startTime,
            endTime
          )} ${durationString(
            getDuration(
              getStartDateTime(date, startTime),
              getEndDateTime(date, startTime, endTime)
            )
          )}`}
        </Text>
        <RoundedButton
          title={
            shift
              ? t('confirmEdit')
              : isAddAndAssign
              ? t('assignShift')
              : t('requestHelp')
          }
          onPress={async () => {
            if (
              preferences.nextShiftSplitterEnabled &&
              !shift &&
              !isAddAndAssign
            ) {
              if (
                getDurationinMinutes(
                  getStartDateTime(date, startTime),
                  getEndDateTime(date, startTime, endTime)
                ) > preferences.nextShiftSplitterLength
              ) {
                const confirmed = await confirmationAlert(
                  t('splitShiftTitle'),
                  t('splitShiftMessage')
                )
                if (confirmed) {
                  return await onSaveShift(true)
                }
              }
            }
            await onSaveShift(false)
          }}
          emphasized
          disabled={!canSave}
          loading={loading}
        />
      </View>
    </SafeAreaView>
  )
}

export const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.colors?.grey6,
    flex: 1,
  },
  sectionHeaderText: {
    fontSize: 16,
    marginVertical: 10,
    marginHorizontal: 15,
  },
  footer: {
    padding: 20,
  },
  footerText: {
    color: theme.colors?.primary,
    fontFamily: font.bold,
    textAlign: 'center',
    marginBottom: 10,
  },
}))
